import React, { useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileSignature, Save, CheckCircle2, Minus } from "lucide-react";

const FIELDS = [
  { key: "contract_number", label: "Contract number", type: "text" },
  { key: "award_date", label: "Award date", type: "date" },
  { key: "ntp_date", label: "Notice to proceed", type: "date" },
  { key: "completion_date", label: "Substantial completion", type: "date" },
  { key: "retainage_pct", label: "Retainage %", type: "number" },
];

export default function ContractTab({ bid, computed, updateBid, readOnly }) {
  const [c, setC] = useState(() => ({ ...(bid.contract || {}) }));
  const [saving, setSaving] = useState(false);
  const [clauses, setClauses] = useState(null);
  const value = computed?.totals?.total ?? bid.stored_total ?? null;
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  React.useEffect(() => {
    api.get(`/bids/${bid.bid_id}/scope-clauses`).then((r) => setClauses(r.data)).catch(() => {});
  }, [bid.bid_id]);

  const save = async () => {
    setSaving(true);
    try {
      await updateBid({ contract: c });
      toast.success("Contract details saved");
    } catch { toast.error("Could not save contract"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-[1500px] w-full mx-auto px-6 sm:px-8 py-6" data-testid="contract-tab">
      <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2 mb-1">
        <FileSignature className="w-5 h-5 text-[#003E87]" /> Contract &amp; award
      </h1>
      <p className="text-sm text-slate-500 mb-5">Award terms and the acceptance record for this bid. The contract value is the priced total.</p>

      <div className="rounded-xl border border-slate-200 overflow-hidden mb-5">
        <div className="bg-black text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Contract value (firm fixed)</div>
            <div className="text-2xl font-mono font-bold text-[#F4C400]" data-testid="contract-value">{value != null ? money(value) : "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Client</div>
            <div className="text-sm font-medium">{bid.client_name || "—"}</div>
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs text-slate-500">{f.label}</label>
              <Input type={f.type} value={c[f.key] ?? ""} disabled={readOnly}
                onChange={(e) => set(f.key, e.target.value)} data-testid={`contract-${f.key}`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Payment terms</label>
            <Input value={c.payment_terms ?? ""} disabled={readOnly} placeholder="e.g. Net 30, monthly progress billing"
              onChange={(e) => set("payment_terms", e.target.value)} data-testid="contract-payment_terms" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">Warranty &amp; notes</label>
            <Textarea rows={3} value={c.notes ?? ""} disabled={readOnly} placeholder="Warranty period, special conditions, exhibits…"
              onChange={(e) => set("notes", e.target.value)} data-testid="contract-notes" />
          </div>
        </div>
      </div>

      {clauses && (clauses.inclusions.length > 0 || clauses.exclusions.length > 0) && (
        <div className="rounded-xl border border-slate-200 p-5 mb-5" data-testid="contract-scope-of-work">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Scope of work</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-emerald-600 mb-1.5">Inclusions</div>
              <ul className="space-y-1.5">
                {clauses.inclusions.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600" data-testid={`clause-inc-${i}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
                {clauses.inclusions.length === 0 && <li className="text-xs text-slate-400">Derived from priced scopes.</li>}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-rose-600 mb-1.5">Exclusions</div>
              <ul className="space-y-1.5">
                {clauses.exclusions.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600" data-testid={`clause-exc-${i}`}>
                    <Minus className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
                {clauses.exclusions.length === 0 && <li className="text-xs text-slate-400">None.</li>}
              </ul>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Auto-derived from the clause library on each priced scope. Edit a scope's clause in Libraries to change this.</p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 p-5 mb-5" data-testid="contract-signatures">        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Acceptance</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {[["Owner / Client", bid.client_name], ["Contractor", "Power Ray LLC"]].map(([role, who]) => (
            <div key={role}>
              <div className="border-b border-slate-300 h-10" />
              <div className="text-xs text-slate-500 mt-1">{role}{who ? ` — ${who}` : ""}</div>
              <div className="text-[11px] text-slate-400">Signature &amp; date</div>
            </div>
          ))}
        </div>
      </div>

      {!readOnly && (
        <Button onClick={save} disabled={saving} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold" data-testid="contract-save">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save contract details"}
        </Button>
      )}
    </div>
  );
}
